import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";
import { http, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Lock, Eye, EyeOff, LogOut, CheckCircle2 } from "lucide-react";

const RULES = [
  ["r10", (p) => p.length >= 10],
  ["rup", (p) => /[A-Z]/.test(p)],
  ["rlo", (p) => /[a-z]/.test(p)],
  ["rnum", (p) => /[0-9]/.test(p)],
  ["rspec", (p) => /[^A-Za-z0-9]/.test(p)],
];
const RULE_LABELS = { r10: "Almeno 10 caratteri", rup: "Una maiuscola", rlo: "Una minuscola", rnum: "Un numero", rspec: "Un carattere speciale" };

export default function ForcePasswordChange() {
  const { logout, refreshUser } = useAuth();
  const { t } = useTranslation();
  const [cur, setCur] = useState("");
  const [np, setNp] = useState("");
  const [cf, setCf] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const allOk = RULES.every(([, fn]) => fn(np)) && np === cf;

  const submit = async () => {
    if (np !== cf) return toast.error(t("errors.password_mismatch"));
    setBusy(true);
    try {
      await http.post("/auth/change-password", { current_password: cur, new_password: np });
      toast.success(t("common.saved"));
      await refreshUser();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0F172A] p-6">
      <div className="w-full max-w-sm bg-white rounded-2xl p-6" data-testid="force-password-screen">
        <div className="flex items-center gap-2 mb-1"><Lock className="w-5 h-5 text-sky-600" /><h1 className="font-heading text-xl font-bold text-[#0F172A]">{t("auth.force_title")}</h1></div>
        <p className="text-sm text-slate-500 mb-4">{t("auth.force_sub")}</p>
        <div className="space-y-3">
          <div><Label className="text-xs">{t("auth.current_password")}</Label><Input data-testid="fpc-current" type={show ? "text" : "password"} value={cur} onChange={(e) => setCur(e.target.value)} className="mt-1" /></div>
          <div>
            <Label className="text-xs">{t("auth.new_password")}</Label>
            <div className="relative mt-1"><Input data-testid="fpc-new" type={show ? "text" : "password"} value={np} onChange={(e) => setNp(e.target.value)} />
              <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" onClick={() => setShow(!show)}>{show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button></div>
          </div>
          <div><Label className="text-xs">{t("auth.confirm_password")}</Label><Input data-testid="fpc-confirm" type={show ? "text" : "password"} value={cf} onChange={(e) => setCf(e.target.value)} className="mt-1" /></div>
          <ul className="text-xs space-y-1 mt-2">
            {RULES.map(([key, fn]) => (
              <li key={key} className={`flex items-center gap-1.5 ${fn(np) ? "text-emerald-600" : "text-slate-400"}`}><CheckCircle2 className="w-3.5 h-3.5" /> {RULE_LABELS[key]}</li>
            ))}
          </ul>
          <Button data-testid="fpc-submit" onClick={submit} disabled={busy || !allOk || !cur} className="w-full bg-[#0F172A] hover:bg-[#1E293B]">{t("auth.change_password")}</Button>
          <Button variant="ghost" onClick={logout} className="w-full text-slate-500"><LogOut className="w-4 h-4 mr-2" /> {t("common.logout")}</Button>
        </div>
      </div>
    </div>
  );
}
